import type { ColumnConfig, PieConfig } from '@ant-design/plots';

import type { SegmentationData } from '~stores/onboarding';
import type { AppTarget } from '~types/app-selection';
import { ApplicationTargetEnum } from '~types/app-selection';
import generatePalette from '~utils/generate-palette';
import {
    BLACK,
    BLUE_800,
    BLUE_SCALE,
    ERROR_800,
    ERROR_SCALE,
    GRAY_100,
} from '~styles/colors';

export const OTHER_SEGMENT_LABEL = 'other';

export type SegmentationChartRow = {
    name: string;
    current: number;
    previous: number;
};

export type PieChartConfigs =
    | {
          isComparing: false;
          showInnerPie: false;
          labels: string[];
          currentColors: string[];
          outerPieConfig: PieConfig;
      }
    | {
          isComparing: true;
          showInnerPie: boolean;
          labels: string[];
          currentColors: string[];
          previousColors: string[];
          outerPieConfig: PieConfig;
          innerPieConfig: PieConfig;
      };

type BarData = {
    label: string;
    value: number;
    type: 'current' | 'previous';
};

type PieData = {
    label: string;
    value: number;
};

const chartTheme = {
    style: {
        fontFamily: 'ACTEasy',
    },
    view: { viewFill: 'transparent' },
};

const axisLabelStyle = {
    labelFill: BLACK,
    labelOpacity: 1,
    labelFontFamily: 'ACTEasy-Medium',
    labelFontSize: 12,
};

const OUTER_RADIUS = 1;
const OUTER_INNER_RADIUS = 0.78;

const INNER_RADIUS = 0.74;
const INNER_INNER_RADIUS = 0.7;

const normalizeSegmentLabel = (label: string) =>
    label.replace(/[\s-]+/g, '').toLowerCase();

const filterCarwashSegment = (
    segments: SegmentationData[],
    appTarget?: AppTarget
) => {
    if (appTarget !== ApplicationTargetEnum.CA) {
        return segments;
    }

    const excluded = new Set([normalizeSegmentLabel('carwash')]);

    return segments.filter(
        (segment) => !excluded.has(normalizeSegmentLabel(segment.name))
    );
};

type AggregatedSegment = {
    label: string;
    total: number;
};

const aggregateSegmentsByKey = (segments: SegmentationData[]) =>
    segments.reduce<Map<string, AggregatedSegment>>((acc, segment) => {
        const key = normalizeSegmentLabel(segment.name);
        const label = segment.name.trim();
        const value = Number(segment.recordCount);
        const safeValue = Number.isFinite(value) ? value : 0;

        const existing = acc.get(key);
        if (existing) {
            existing.total += safeValue;
        } else {
            acc.set(key, { label, total: safeValue });
        }

        return acc;
    }, new Map());

const getColumnModeConfig = (compare: boolean): Partial<ColumnConfig> =>
    compare
        ? {
              group: { padding: 0 },
              seriesField: 'type',
              colorField: 'type',
              scale: {
                  color: {
                      domain: ['previous', 'current'],
                      range: [ERROR_800, BLUE_800],
                  },
              },
          }
        : {
              group: false,
              style: {
                  fill: BLUE_800,
                  radiusTopLeft: 10,
                  radiusTopRight: 10,
              },
          };

const buildBarChartData = (
    chartData: SegmentationChartRow[],
    isComparing: boolean
): BarData[] => {
    const barChartData: BarData[] = [];

    chartData.forEach((segment) => {
        if (isComparing) {
            barChartData.push({
                label: segment.name,
                type: 'previous',
                value: segment.previous,
            });
        }

        barChartData.push({
            label: segment.name,
            type: 'current',
            value: segment.current,
        });
    });

    return barChartData;
};

export const buildSegmentationData = (
    current: SegmentationData[],
    previous: SegmentationData[],
    isComparing: boolean,
    appTarget?: AppTarget
): SegmentationChartRow[] => {
    const filteredCurrent = filterCarwashSegment(current, appTarget);
    const filteredPrevious = filterCarwashSegment(previous, appTarget);

    const currentByKey = aggregateSegmentsByKey(filteredCurrent);
    const previousByKey = isComparing
        ? aggregateSegmentsByKey(filteredPrevious)
        : new Map<string, AggregatedSegment>();
    const keys = new Set<string>([
        ...currentByKey.keys(),
        ...(isComparing ? previousByKey.keys() : []),
    ]);

    return Array.from(keys)
        .map((key) => {
            const currentEntry = currentByKey.get(key);
            const previousEntry = previousByKey.get(key);
            const name = currentEntry?.label ?? previousEntry?.label ?? key;

            return {
                name,
                current: currentEntry?.total ?? 0,
                previous: isComparing ? previousEntry?.total ?? 0 : 0,
            };
        })
        .filter((row) => row.current + row.previous > 0)
        .sort((a, b) => {
            const aIsOther =
                normalizeSegmentLabel(a.name) === OTHER_SEGMENT_LABEL;
            const bIsOther =
                normalizeSegmentLabel(b.name) === OTHER_SEGMENT_LABEL;
            if (aIsOther === bIsOther) return 0;
            return aIsOther ? 1 : -1;
        });
};

export const buildColumnChartConfig = (
    chartData: SegmentationChartRow[],
    isComparing: boolean
): ColumnConfig => {
    const barChartData = buildBarChartData(chartData, isComparing);

    const columnTooltipConfig: ColumnConfig['tooltip'] = {
        title: { field: 'label' },
        items: isComparing
            ? [
                  (data: BarData) => ({
                      name: data.type === 'current' ? 'Current' : 'Previous',
                      value: data.value,
                      color: data.type === 'current' ? BLUE_800 : ERROR_800,
                  }),
              ]
            : [
                  (data: BarData) => ({
                      name: 'Count',
                      value: data.value,
                      color: BLUE_800,
                  }),
              ],
    };

    return {
        theme: chartTheme,
        title: 'Which features are you most interested in?',
        tooltip: columnTooltipConfig,
        legend: false,
        xField: 'label',
        yField: 'value',
        axis: {
            x: axisLabelStyle,
            y: axisLabelStyle,
        },
        data: barChartData,
        style: { radiusTopLeft: 10, radiusTopRight: 10 },
        ...getColumnModeConfig(isComparing),
    };
};

export const buildPieChartConfigs = (
    chartData: SegmentationChartRow[],
    isComparing: boolean
): PieChartConfigs => {
    const hasPreviousData =
        isComparing && chartData.some((segment) => segment.previous > 0);
    const labels = chartData.map((segment) => segment.name);

    const pieCurrentData: PieData[] = chartData.map((segment) => ({
        label: segment.name,
        value: segment.current,
    }));

    const piePreviousData: PieData[] = isComparing
        ? chartData.map((segment) => ({
              label: segment.name,
              value: segment.previous,
          }))
        : [];

    const currentColors = generatePalette(
        [...BLUE_SCALE].reverse(),
        labels.length
    );
    const previousColors = isComparing
        ? generatePalette([...ERROR_SCALE].reverse(), labels.length)
        : [];
    const otherIndex = labels.findIndex(
        (label) => label.toLowerCase() === OTHER_SEGMENT_LABEL
    );

    if (otherIndex >= 0) {
        currentColors.splice(otherIndex, 1, GRAY_100);
        if (isComparing) {
            previousColors.splice(otherIndex, 1, GRAY_100);
        }
    }

    const labelIndex = labels.reduce<Record<string, number>>(
        (acc, label, index) => {
            acc[label] = index;
            return acc;
        },
        {}
    );

    const getColorForLabel = (palette: string[], label: string) =>
        palette[labelIndex[label] ?? 0];

    const tooltipMount = typeof document !== 'undefined' ? 'body' : undefined;

    const pieTooltipConfig: PieConfig['tooltip'] = {
        title: { field: 'label' },
        items: isComparing
            ? [
                  (data: PieData) => ({
                      name: 'Current',
                      value: data.value,
                      color: getColorForLabel(currentColors, data.label),
                  }),
                  (data: PieData) => {
                      const match = chartData.find(
                          (row) => row.name === data.label
                      );
                      return {
                          name: 'Previous',
                          value: match?.previous ?? 0,
                          color: getColorForLabel(previousColors, data.label),
                      };
                  },
              ]
            : [
                  (data: PieData) => ({
                      name: 'Count',
                      value: data.value,
                      color: getColorForLabel(currentColors, data.label),
                  }),
              ],
    };

    const outerPieConfig: PieConfig = {
        data: pieCurrentData,
        angleField: 'value',
        colorField: 'label',
        radius: OUTER_RADIUS,
        innerRadius: isComparing ? OUTER_INNER_RADIUS : 0.75,
        label: false,
        tooltip: pieTooltipConfig,
        legend: false,
        transform: [{ type: 'stackY', reverse: false }],
        interaction: {
            tooltip: {
                mount: tooltipMount,
            },
        },
        theme: chartTheme,
        scale: {
            color: {
                domain: labels,
                range: currentColors,
            },
        },
    };

    if (!isComparing) {
        return {
            isComparing: false,
            showInnerPie: false,
            labels,
            currentColors,
            outerPieConfig,
        };
    }

    const innerPieConfig: PieConfig = {
        data: piePreviousData,
        angleField: 'value',
        colorField: 'label',
        radius: INNER_RADIUS,
        innerRadius: INNER_INNER_RADIUS,
        label: false,
        tooltip: false,
        legend: false,
        transform: [{ type: 'stackY', reverse: false }],
        theme: chartTheme,
        scale: {
            color: {
                domain: labels,
                range: previousColors,
            },
        },
    };

    return {
        isComparing: true,
        showInnerPie: hasPreviousData,
        labels,
        currentColors,
        previousColors,
        outerPieConfig,
        innerPieConfig,
    };
};
